import React, { useEffect, useRef, useState } from 'react';
import { X, Sparkles, ChevronDown, CheckCircle2, AlertCircle } from 'lucide-react';
import Modal from '../../../components/Modal';
import { Select, Button } from '../../../components';
import careNeedsService from '../../../services/carehome_manager/careNeedsService';
import { useAuth } from '../../../context/AuthContext';
import {
    CARE_PLAN_CATEGORIES, PRIORITY_OPTIONS, STATUS_OPTIONS, NEEDS_REVIEW_DATE,
    categoryLabel, formatDate, todayISO,
} from './carePlanConstants';

/**
 * Create / edit a care plan — one editor for both, plus "Write with Patch".
 *
 * The editor works on a list of drafts (`working`). Creating by hand is a list
 * of one; Patch can turn one set of notes into several drafts (one per care
 * need), each reviewed, saved and approved on its own from the pill bar.
 * Editing an approved plan uses Patch differently ("Update with Patch"): it
 * revises THIS plan's wording from notes on what changed, in place, with Undo.
 *
 * Save draft never changes what carers see (see carePlans.controller.js).
 * Approve & save requires every field, a future review date for a live plan,
 * a summary for the history and the authorisation tick — and, for a Patch
 * draft, an explicit "I have checked this suggested category".
 */

let keySeq = 0;
const newKey = () => `draft-${Date.now()}-${++keySeq}`;

export const blankDraft = (category = '') => ({
    key: newKey(),
    planId: null,          // server id once saved
    baseVersion: 0,        // version this edit started from (approval conflict check)
    neverApproved: true,   // category can still change
    category,
    title: '',
    currentSituation: '',
    expectedOutcome: '',
    actions: '',
    priority: '',          // deliberately no default: someone has to choose
    responsiblePersonId: '', // the editor fills in the signed-in user (see withDefaults)
    responsibleName: '',   // name for a responsible person no longer on the review team
    createdByName: '',     // read-only; a new plan is created by whoever is signed in
    status: '',
    nextReviewDate: '',
    lastReviewedAt: null,
    summary: '',
    generated: false,      // new plan drafted by Patch (category needs checking)
    revised: false,        // existing plan's wording updated by Patch
    categoryConfirmed: false,
    authorised: false,
    sourceNotes: '',
    questions: [],
    saved: false,
    approved: false,
    changed: false,
});

/**
 * Care actions are shown as bullet points so they read like the checklist they
 * are. The bullets are display only: they are stripped before saving (the API
 * stores one action per line, which is what the mobile app splits on).
 */
const BULLET = '• ';
const stripBullet = (line) => line.replace(/^\s*[-•*]\s*/, '');
export const toBullets = (text) => String(text || '')
    .split(/\r?\n/)
    .map((line) => stripBullet(line).trim())
    .filter(Boolean)
    .map((line) => BULLET + line)
    .join('\n');
export const fromBullets = (text) => String(text || '')
    .split(/\r?\n/)
    .map((line) => stripBullet(line).trim())
    .filter(Boolean)
    .join('\n');

/** An editor draft from a stored plan, preferring its unapproved edits if it has any. */
export const draftFromPlan = (plan) => {
    const d = plan.details || {};
    const pending = d.draft || null;
    const neverApproved = plan.status === 'draft';
    const src = pending || {
        title: plan.title,
        currentSituation: d.currentSituation,
        expectedOutcome: d.expectedOutcome,
        actions: d.actions,
        priority: plan.priority,
        responsible_person_id: plan.responsible_person_id,
        status: plan.status,
        nextReviewDate: d.nextReviewDate,
        summary: '',
    };
    return {
        ...blankDraft(),
        planId: plan.id,
        baseVersion: d.version || 0,
        neverApproved,
        category: (neverApproved && pending?.category_name) || plan.category_id,
        title: src.title || plan.title || '',
        currentSituation: src.currentSituation || '',
        expectedOutcome: src.expectedOutcome || '',
        actions: toBullets(src.actions),
        priority: src.priority || '',
        responsiblePersonId: src.responsible_person_id ? String(src.responsible_person_id) : '',
        responsibleName: String(src.responsible_person_id) === String(plan.responsible_person_id) ? plan.responsiblePerson?.name || '' : '',
        createdByName: plan.creator?.name || '',
        status: src.status && src.status !== 'draft' ? src.status : '',
        nextReviewDate: src.nextReviewDate ? String(src.nextReviewDate).slice(0, 10) : '',
        lastReviewedAt: d.lastReviewedAt || null,
        summary: src.summary || '',
        generated: !!(neverApproved && pending?.patchAssisted),
        revised: !!(!neverApproved && pending?.patchAssisted),
        sourceNotes: pending?.sourceNotes || '',
        saved: !!pending,
    };
};

const inputCls = 'w-full bg-white dark:bg-[#1f2329] border border-gray-200 dark:border-gray-700 rounded-lg px-3.5 py-2.5 text-[13.5px] text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 disabled:opacity-60 disabled:cursor-not-allowed';
const labelCls = 'block text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5';
const helpCls = 'mt-1 text-[12px] text-gray-500 dark:text-gray-400';
const Req = () => <span className="text-red-500">*</span>;

/** The wording "Update with Patch" rewrites — and Undo puts back. */
const CONTENT_FIELDS = ['title', 'currentSituation', 'expectedOutcome', 'actions'];

const PatchTag = () => (
    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
        Patch draft · review needed
    </span>
);

const CarePlanEditor = ({ isOpen, onClose, residenceId, residentName, reviewers, initialDrafts, onSaved }) => {
    const { user } = useAuth();
    const myId = user?.id ? String(user.id) : '';
    const myName = user?.name || '';
    const [working, setWorking] = useState([]);
    const [index, setIndex] = useState(0);
    const [confirmClose, setConfirmClose] = useState(false);
    const [feedback, setFeedback] = useState(null); // { text, type }
    const [saving, setSaving] = useState(null);     // 'draft' | 'approve' | 'discard'

    const [patchOpen, setPatchOpen] = useState(false);
    const [notes, setNotes] = useState('');
    const [patchBusy, setPatchBusy] = useState(false);
    const [patchFeedback, setPatchFeedback] = useState(null);
    const [replaceConfirm, setReplaceConfirm] = useState(false);
    const [patchResult, setPatchResult] = useState('');
    const [patchUndo, setPatchUndo] = useState(null); // { key, before } — the wording before "Update with Patch"

    const bodyRef = useRef(null);

    // A new draft is created by whoever is signed in, and anything without a
    // responsible person starts with them. Not an edit: closing without
    // saving still asks nothing.
    const withDefaults = (x) => ({
        ...x,
        createdByName: x.createdByName || (x.planId ? '' : myName),
        responsiblePersonId: x.responsiblePersonId || (x.approved ? '' : myId),
    });

    useEffect(() => {
        if (!isOpen) return;
        setWorking((initialDrafts?.length ? initialDrafts : [blankDraft()]).map(withDefaults));
        setIndex(0);
        setConfirmClose(false);
        setFeedback(null);
        setPatchOpen(false);
        setNotes('');
        setPatchFeedback(null);
        setReplaceConfirm(false);
        setPatchResult('');
        setPatchUndo(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialDrafts]);

    const d = working[index];
    if (!isOpen || !d) return null;

    const isEdit = !!d.planId && !d.neverApproved;
    const locked = d.approved;
    const dirty = working.some((x) => x.changed && !x.approved);

    const update = (patch) => {
        setWorking((list) => list.map((x, i) => {
            if (i !== index) return x;
            const next = { ...x, ...patch, changed: true, saved: false };
            // Any change to the content withdraws the authorisation;
            // a new category withdraws the category check.
            if (!('authorised' in patch)) next.authorised = false;
            if ('category' in patch) next.categoryConfirmed = false;
            return next;
        }));
        setFeedback(null);
        // Undo only puts back Patch's change; once the manager edits the wording
        // by hand, undoing would throw their typing away too.
        if (CONTENT_FIELDS.some((k) => k in patch)) setPatchUndo(null);
    };

    const replaceCurrent = (patch) =>
        setWorking((list) => list.map((x, i) => (i === index ? { ...x, ...patch } : x)));

    const requestClose = () => {
        if (dirty) setConfirmClose(true);
        else onClose();
    };

    const fail = (text) => {
        setFeedback({ text, type: 'error' });
        return false;
    };

    // ── Patch ────────────────────────────────────────────────────────────────

    const hasContent = working.some((x) => !x.approved && (x.title || x.currentSituation || x.expectedOutcome || fromBullets(x.actions)));

    const runPatch = async (force = false) => {
        const text = notes.trim();
        if (!text) { setPatchFeedback({ text: 'Describe the person’s needs first.', type: 'error' }); return; }
        if (text.length > 20000) { setPatchFeedback({ text: 'Use up to 20,000 characters.', type: 'error' }); return; }
        if (hasContent && !force) { setReplaceConfirm(true); return; }
        setReplaceConfirm(false);
        setPatchBusy(true);
        setPatchFeedback(null);
        try {
            const res = await careNeedsService.patchDrafts(residenceId, text);
            const generated = res?.data?.drafts || [];
            // Keep the review fields already chosen for a draft in the same category.
            const previous = working.filter((x) => !x.approved);
            const used = new Set();
            const next = generated.map((g) => {
                const draft = {
                    ...withDefaults(blankDraft(g.category)),
                    title: g.title,
                    currentSituation: g.currentSituation,
                    expectedOutcome: g.expectedOutcome,
                    actions: toBullets((g.actions || []).join('\n')),
                    questions: [
                        ...(!g.category ? ['Choose a category — Patch could not place this need reliably.'] : []),
                        ...(g.questions || []),
                    ],
                    generated: true,
                    sourceNotes: text,
                    changed: true,
                };
                const i = previous.findIndex((p, pi) => !used.has(pi) && p.category && p.category === g.category);
                if (i >= 0) {
                    used.add(i);
                    const p = previous[i];
                    Object.assign(draft, {
                        planId: p.planId, baseVersion: p.baseVersion, neverApproved: p.neverApproved,
                        priority: p.priority,
                        responsiblePersonId: p.responsiblePersonId || draft.responsiblePersonId,
                        responsibleName: p.responsibleName, createdByName: p.createdByName || draft.createdByName,
                        status: p.status, nextReviewDate: p.nextReviewDate,
                    });
                }
                return draft;
            });
            setWorking([...working.filter((x) => x.approved), ...next]);
            setIndex(working.filter((x) => x.approved).length);
            setPatchOpen(false);
            setPatchResult(`${next.length} draft${next.length === 1 ? ' is' : 's are'} ready to review. Check the suggested category and edit the content below — nothing is saved until you save or approve it.`);
            bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            const notThere = err?.response?.status === 404;
            setPatchFeedback({
                text: notThere
                    ? 'Patch drafting is not available on this server yet — the API needs updating. Your notes are unchanged.'
                    : err?.response?.data?.message || 'Patch could not draft these notes. Your notes are unchanged.',
                type: 'error',
            });
        } finally {
            setPatchBusy(false);
        }
    };

    /** Update with Patch: revise the open plan in place from notes on what changed. */
    const runRevise = async () => {
        const text = notes.trim();
        if (text.length > 20000) { setPatchFeedback({ text: 'Use up to 20,000 characters.', type: 'error' }); return; }
        setPatchBusy(true);
        setPatchFeedback(null);
        try {
            const res = await careNeedsService.patchRevise(residenceId, d.planId, text, {
                title: d.title,
                currentSituation: d.currentSituation,
                expectedOutcome: d.expectedOutcome,
                actions: fromBullets(d.actions),
            });
            const p = res?.data?.plan;
            if (!p) throw new Error('No plan returned');
            const before = {
                ...Object.fromEntries(CONTENT_FIELDS.map((k) => [k, d[k]])),
                questions: d.questions, sourceNotes: d.sourceNotes, revised: d.revised,
            };
            update({
                title: p.title || d.title,
                currentSituation: p.currentSituation || '',
                expectedOutcome: p.expectedOutcome || '',
                actions: toBullets((p.actions || []).join('\n')),
                questions: p.questions || [],
                sourceNotes: text,
                revised: true,
            });
            setPatchUndo({ key: d.key, before });
            setPatchOpen(false);
            setNotes('');
            setPatchResult('Patch has updated this plan. Check every box below before you approve — nothing is saved until you save or approve it.');
            bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            // The API's catch-all 404 — as opposed to "Care plan not found".
            const notThere = err?.response?.status === 404 && err?.response?.data?.message === 'Route not found';
            setPatchFeedback({
                text: notThere
                    ? 'Updating with Patch is not available on this server yet — the API needs updating. The plan is unchanged.'
                    : err?.response?.data?.message || 'Patch could not update this plan. The plan is unchanged.',
                type: 'error',
            });
        } finally {
            setPatchBusy(false);
        }
    };

    const undoRevise = () => {
        if (!patchUndo) return;
        update(patchUndo.before);
        setPatchUndo(null);
        setPatchResult('');
    };

    // ── Save / approve ───────────────────────────────────────────────────────

    const payload = (mode) => ({
        mode,
        category_name: d.category,
        title: d.title.trim(),
        currentSituation: d.currentSituation.trim(),
        expectedOutcome: d.expectedOutcome.trim(),
        actions: fromBullets(d.actions),
        priority: d.priority,
        responsible_person_id: d.responsiblePersonId || null,
        status: d.status,
        nextReviewDate: d.nextReviewDate,
        summary: d.summary.trim(),
        patchAssisted: d.generated || d.revised,
        sourceNotes: d.sourceNotes,
        authorised: d.authorised,
        baseVersion: d.baseVersion,
    });

    const persist = async (mode) => {
        const body = payload(mode);
        const res = d.planId
            ? await careNeedsService.updateCareNeed(residenceId, d.planId, body)
            : await careNeedsService.createCareNeed(residenceId, body);
        return res?.data;
    };

    const saveDraft = async () => {
        if (!d.category) return fail('Choose a category before saving this draft.');
        if (!d.title.trim()) return fail('Give this care plan a title before saving the draft.');
        setSaving('draft');
        try {
            const plan = await persist('draft');
            replaceCurrent({ planId: plan?.id ?? d.planId, saved: true, changed: false, authorised: false });
            setFeedback({ text: 'Draft saved. It is not visible to carers until it is approved.', type: 'success' });
            onSaved?.(plan, { approved: false });
        } catch (err) {
            fail(err?.response?.data?.message || 'Could not save the draft.');
        } finally {
            setSaving(null);
        }
    };

    const approve = async (e) => {
        e?.preventDefault();
        if (locked) return;
        const checks = [
            [d.category, 'Choose a category.'],
            [d.title.trim(), 'Enter a care plan title.'],
            [d.currentSituation.trim(), 'Complete the current situation.'],
            [d.expectedOutcome.trim(), 'Complete the agreed expected outcome.'],
            [fromBullets(d.actions), 'Enter the agreed care actions.'],
            [d.priority, 'Choose the priority — Patch never sets this.'],
            [d.responsiblePersonId, 'Choose who is responsible for review.'],
            [d.status, 'Choose the status after approval.'],
        ];
        for (const [ok, msg] of checks) if (!ok) return fail(msg);
        if (NEEDS_REVIEW_DATE.includes(d.status) && (!d.nextReviewDate || d.nextReviewDate <= todayISO())) {
            return fail('Choose a future review date for an active or on-hold plan.');
        }
        if (!d.summary.trim()) return fail('Add a summary of changes for the review history.');
        if (d.generated && !d.categoryConfirmed) return fail('Confirm the suggested category before approval.');
        if (!d.authorised) return fail('Review the plan and tick the authorisation box.');

        setSaving('approve');
        try {
            const plan = await persist('approve');
            const next = working.map((x, i) => (i === index
                ? { ...x, planId: plan?.id ?? x.planId, approved: true, saved: true, changed: false }
                : x));
            setWorking(next);
            setFeedback({
                text: `“${d.title.trim()}” approved. ${next.some((x) => !x.approved) ? 'Select the next draft to review.' : 'Carers will see it in the app.'}`,
                type: 'success',
            });
            onSaved?.(plan, { approved: true });
        } catch (err) {
            fail(err?.response?.data?.message || 'Could not approve this care plan.');
        } finally {
            setSaving(null);
        }
    };

    const discard = async () => {
        if (!d.planId || !d.saved) return;
        setSaving('discard');
        try {
            await careNeedsService.discardDraft(residenceId, d.planId);
            onSaved?.(null, { discarded: true });
            const rest = working.filter((_, i) => i !== index);
            if (!rest.length) { onClose(); return; }
            setWorking(rest);
            setIndex(0);
        } catch (err) {
            fail(err?.response?.data?.message || 'Could not discard the draft.');
        } finally {
            setSaving(null);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const subtitle = [
        working.length > 1 ? `Draft ${index + 1} of ${working.length}` : null,
        d.approved ? 'Approved' : d.saved ? 'Saved draft' : d.changed ? 'Unsaved changes' : isEdit ? 'Approved version' : 'New draft',
    ].filter(Boolean).join(' · ');

    // The review team: everyone with dashboard access to this site. Someone
    // chosen before the list was limited to dashboard users stays selectable
    // (marked) until a manager picks somebody else.
    const reviewerOptions = (() => {
        const team = (reviewers || []).map((p) => ({
            value: String(p.id),
            label: `${p.name}${String(p.id) === myId ? ' (you)' : ''}${p.role ? ` — ${p.role}` : ''}`,
        }));
        const current = d.responsiblePersonId;
        if (current && !team.some((o) => o.value === current)) {
            team.unshift({
                value: current,
                label: current === myId ? `${myName} (you)` : `${d.responsibleName || 'Someone'} (previously chosen)`,
            });
        }
        return [{ value: '', label: 'Choose a person' }, ...team];
    })();

    return (
        <Modal isOpen={isOpen} onClose={requestClose} hideHeader maxWidth="max-w-[760px]" bodyClassName="flex flex-col max-h-[calc(100dvh-4rem)]">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
                <div className="min-w-0">
                    <h2 className="text-[17px] font-semibold text-gray-800 dark:text-gray-100">{isEdit ? 'Edit care plan' : 'Create care plan'}</h2>
                    <p className="text-[12.5px] text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}{residentName ? ` · ${residentName}` : ''}</p>
                </div>
                <button type="button" onClick={requestClose} aria-label="Close care plan editor"
                    className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 flex items-center justify-center flex-shrink-0">
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
                {confirmClose && (
                    <div className="mb-5 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-4">
                        <p className="text-[13px] text-amber-800 dark:text-amber-300 mb-3">Leave this editor? Changes not saved as a draft will be lost.</p>
                        <div className="flex gap-2">
                            <Button size="sm" variant="secondary" onClick={() => setConfirmClose(false)}>Keep editing</Button>
                            <Button size="sm" variant="danger-soft" onClick={onClose}>Leave editor</Button>
                        </div>
                    </div>
                )}

                {working.length > 1 && (
                    <div className="mb-5">
                        <p className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 mb-2">{working.length} drafts · review and save each separately</p>
                        <div className="flex flex-wrap gap-2">
                            {working.map((x, i) => (
                                <button key={x.key} type="button" onClick={() => { setIndex(i); setFeedback(null); }} aria-pressed={i === index}
                                    className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition ${i === index
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white dark:bg-[#1f2329] text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-blue-300'}`}>
                                    {i + 1}. {x.title || categoryLabel(x.category)}
                                    {x.approved ? ' · Approved' : x.saved ? ' · Saved' : ''}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {patchResult && !d.approved && (
                    <div className="mb-5 flex gap-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 px-4 py-3 text-[13px] text-blue-800 dark:text-blue-200">
                        <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span className="flex-1 min-w-0">{patchResult}</span>
                        {patchUndo?.key === d.key && (
                            <button type="button" onClick={undoRevise}
                                className="self-start flex-shrink-0 text-[12.5px] font-semibold text-blue-700 dark:text-blue-300 underline underline-offset-2 hover:text-blue-900 dark:hover:text-blue-100">
                                Undo
                            </button>
                        )}
                    </div>
                )}

                {d.approved && (
                    <div className="mb-5 flex gap-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40 px-4 py-3 text-[13px] text-emerald-800 dark:text-emerald-300">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        This plan is approved. {working.some((x) => !x.approved) ? 'Select the next draft to review.' : 'All drafts in this set have been reviewed.'}
                    </div>
                )}

                <form id="care-plan-form" onSubmit={approve} noValidate>
                    <fieldset disabled={locked} className="space-y-5 min-w-0">
                        {/* Category */}
                        <div>
                            <label className={labelCls}>Category <Req /></label>
                            {d.neverApproved ? (
                                <Select value={d.category} onChange={(v) => update({ category: v })} disabled={locked}
                                    placeholder="Select a category"
                                    options={CARE_PLAN_CATEGORIES.map((c) => ({ value: c.id, label: c.label }))}
                                    className="w-full" />
                            ) : (
                                <>
                                    <input type="text" readOnly value={categoryLabel(d.category)} className={`${inputCls} bg-gray-50 dark:bg-[#1b1f26]`} aria-label="Category" />
                                    <p className={helpCls}>An existing plan stays in its original category.</p>
                                </>
                            )}
                            {d.generated && (
                                <label className="mt-2.5 flex items-start gap-2.5 text-[13px] text-gray-700 dark:text-gray-300 cursor-pointer">
                                    <input type="checkbox" checked={d.categoryConfirmed}
                                        onChange={(e) => replaceCurrent({ categoryConfirmed: e.target.checked, authorised: false })}
                                        className="mt-0.5 w-4 h-4 accent-blue-600" />
                                    I have checked this suggested category.
                                </label>
                            )}
                        </div>

                        {/* Title */}
                        <div>
                            <label className={labelCls} htmlFor="cp-title">Care plan title <Req /></label>
                            <input id="cp-title" type="text" maxLength={120} value={d.title} onChange={(e) => update({ title: e.target.value })}
                                placeholder="e.g. Washing & dressing, Diabetes care or Falls prevention" className={inputCls} />
                            <p className={helpCls}>Keep specific needs as separate plans within the same category.</p>
                        </div>

                        {/* Write with Patch — new plans: notes become drafts; an
                            existing plan: notes on what changed revise it in place. */}
                        {!locked && (
                            <div className="rounded-xl border border-blue-100 dark:border-blue-900/50 bg-[#F7F9FF] dark:bg-blue-950/20">
                                <button type="button" onClick={() => setPatchOpen((o) => !o)} aria-expanded={patchOpen}
                                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left">
                                    <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-[14px] font-semibold text-gray-800 dark:text-gray-100">{isEdit ? 'Update with Patch' : 'Write with Patch'}</span>
                                        <span className="block text-[12.5px] text-gray-500 dark:text-gray-400">
                                            {isEdit ? 'Tell Patch what has changed, or let it tidy the wording' : 'Turn your notes into a draft you can review and edit'}
                                        </span>
                                    </span>
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${patchOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {patchOpen && isEdit && (
                                    <div className="px-4 pb-4 pt-1 border-t border-blue-100 dark:border-blue-900/50 space-y-3">
                                        <div className="pt-3">
                                            <label className={labelCls} htmlFor="cp-notes">What has changed? <span className="font-normal text-gray-400">(optional)</span></label>
                                            <textarea id="cp-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                                                placeholder="e.g. Since the fall on 2 October, needs two carers to stand. No longer uses the shower chair. Leave blank to just fix spelling and wording."
                                                className={`${inputCls} resize-y`} />
                                            <p className={helpCls}>Patch updates this plan only and keeps everything that still applies. It never changes the category, priority, status or review date.</p>
                                        </div>
                                        {patchFeedback && (
                                            <p className={`text-[12.5px] ${patchFeedback.type === 'error' ? 'text-red-600 dark:text-red-400' : 'text-emerald-600'}`}>{patchFeedback.text}</p>
                                        )}
                                        <div className="flex justify-end">
                                            <Button size="sm" variant="primary" loading={patchBusy} onClick={runRevise} leftIcon={<Sparkles className="w-3.5 h-3.5" />}>
                                                {notes.trim() ? 'Update plan' : 'Tidy wording'}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                {patchOpen && !isEdit && (
                                    <div className="px-4 pb-4 pt-1 border-t border-blue-100 dark:border-blue-900/50 space-y-3">
                                        <div className="pt-3">
                                            <label className={labelCls} htmlFor="cp-notes">Describe the person&rsquo;s needs in your own words</label>
                                            <textarea id="cp-notes" rows={5} value={notes} onChange={(e) => { setNotes(e.target.value); setReplaceConfirm(false); }}
                                                placeholder="Tell Patch what the person can do, what support they need, their preferences and their goals. You can include more than one care need."
                                                className={`${inputCls} resize-y`} />
                                            <p className={helpCls}>Different needs become separate drafts. Patch only uses what you write — it never sets priority, status or review dates.</p>
                                        </div>
                                        {replaceConfirm && (
                                            <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3">
                                                <p className="text-[12.5px] text-amber-800 dark:text-amber-300 mb-2.5">Generate a new set of drafts? Unsaved draft content in this editor will be replaced. Saved plans stay as they are.</p>
                                                <div className="flex gap-2">
                                                    <Button size="sm" variant="secondary" onClick={() => setReplaceConfirm(false)}>Keep these drafts</Button>
                                                    <Button size="sm" variant="primary" onClick={() => runPatch(true)}>Replace unsaved drafts</Button>
                                                </div>
                                            </div>
                                        )}
                                        {patchFeedback && (
                                            <p className={`text-[12.5px] ${patchFeedback.type === 'error' ? 'text-red-600 dark:text-red-400' : 'text-emerald-600'}`}>{patchFeedback.text}</p>
                                        )}
                                        <div className="flex justify-end">
                                            <Button size="sm" variant="primary" loading={patchBusy} onClick={() => runPatch(false)} leftIcon={<Sparkles className="w-3.5 h-3.5" />}>
                                                Generate drafts
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {d.sourceNotes && (
                            <details className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2.5">
                                <summary className="text-[12.5px] font-medium text-gray-600 dark:text-gray-300 cursor-pointer">Source notes used for this draft</summary>
                                <p className="mt-2 text-[12.5px] text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words">{d.sourceNotes}</p>
                            </details>
                        )}

                        {d.questions?.length > 0 && !locked && (
                            <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
                                <p className="flex items-center gap-1.5 text-[13px] font-semibold text-amber-800 dark:text-amber-300"><AlertCircle className="w-4 h-4" /> Details to confirm</p>
                                <ul className="mt-1.5 list-disc pl-5 text-[12.5px] text-amber-800 dark:text-amber-300 space-y-0.5">
                                    {d.questions.map((q, i) => <li key={i}>{q}</li>)}
                                </ul>
                                <p className="mt-1.5 text-[12px] text-amber-700/80 dark:text-amber-400/80">Add the confirmed information to the fields below before approval.</p>
                            </div>
                        )}

                        {/* Content */}
                        {[
                            ['currentSituation', 'Current situation', 3, 'Needs, independence and preferences', null],
                            ['expectedOutcome', 'Expected outcome', 2, 'The person’s agreed goal', null],
                            ['actions', 'Care actions', 5, '• What carers need to do — press Enter for the next action', 'One action per bullet. Shown to carers in the app under “What I need to do”.'],
                        ].map(([name, label, rows, placeholder, help]) => (
                            <div key={name}>
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                                    <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-300" htmlFor={`cp-${name}`}>{label} <Req /></label>
                                    {(d.generated || d.revised) && !locked && <PatchTag />}
                                </div>
                                <textarea id={`cp-${name}`} rows={rows} value={d[name]}
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        // The first thing typed into an empty actions box becomes a bullet.
                                        if (name === 'actions' && !d.actions && value && !value.startsWith(BULLET.trim())) value = BULLET + value;
                                        update({ [name]: value });
                                    }}
                                    onKeyDown={name === 'actions' ? (e) => {
                                        // Enter starts the next bullet.
                                        if (e.key !== 'Enter' || e.shiftKey) return;
                                        e.preventDefault();
                                        // Inserted in place (the cursor lands after the new bullet), then
                                        // state catches up with the same value — so React never moves
                                        // the cursor and fast typing cannot land in the wrong spot.
                                        const el = e.currentTarget;
                                        el.setRangeText(`\n${BULLET}`, el.selectionStart, el.selectionEnd, 'end');
                                        update({ actions: el.value });
                                    } : undefined}
                                    placeholder={placeholder} className={`${inputCls} resize-y`} />
                                {help && <p className={helpCls}>{help}</p>}
                            </div>
                        ))}

                        {/* Review & approval */}
                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                            <h3 className="text-[14.5px] font-semibold text-gray-800 dark:text-gray-100">Review &amp; approval</h3>
                            <p className="text-[12.5px] text-gray-500 dark:text-gray-400 mt-0.5">Assign responsibility and confirm when this plan should be reviewed.</p>
                        </div>

                        {/* Three pairs: what (priority, status), who (responsible, creator), when (reviews). */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <span className={labelCls}>Priority <Req /></span>
                                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Priority">
                                    {PRIORITY_OPTIONS.map((opt) => (
                                        <label key={opt.value} className={`px-4 py-2 rounded-lg border text-[13px] font-medium cursor-pointer transition ${d.priority === opt.value
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white dark:bg-[#1f2329] text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-blue-300'}`}>
                                            <input type="radio" name="cp-priority" value={opt.value} checked={d.priority === opt.value}
                                                onChange={() => update({ priority: opt.value })} className="sr-only" />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>Status after approval <Req /></label>
                                <Select value={d.status} onChange={(v) => update({ status: v })} disabled={locked}
                                    placeholder="Choose a status" options={STATUS_OPTIONS} className="w-full" />
                                <p className={helpCls}>Active: follow now. On hold: temporarily paused. Achieved: goal completed. Discontinued: no longer applicable.</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelCls}>Responsible for review <Req /></label>
                                <Select value={d.responsiblePersonId} onChange={(v) => update({ responsiblePersonId: v })}
                                    disabled={locked} options={reviewerOptions} className="w-full" />
                                <p className={helpCls}>Anyone with dashboard access. Starts as you.</p>
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="cp-created">Created by</label>
                                <input id="cp-created" type="text" readOnly value={d.createdByName || 'Not recorded'}
                                    className={`${inputCls} bg-gray-50 dark:bg-[#1b1f26]`} />
                                <p className={helpCls}>Recorded automatically and cannot be changed.</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelCls} htmlFor="cp-last">Last reviewed</label>
                                <input id="cp-last" type="text" readOnly value={d.lastReviewedAt ? formatDate(d.lastReviewedAt) : 'Not yet reviewed'}
                                    className={`${inputCls} bg-gray-50 dark:bg-[#1b1f26]`} />
                                <p className={helpCls}>Set automatically when the plan is approved.</p>
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="cp-next">Next review date{NEEDS_REVIEW_DATE.includes(d.status) && <> <Req /></>}</label>
                                <input id="cp-next" type="date" value={d.nextReviewDate} min={todayISO()}
                                    onChange={(e) => update({ nextReviewDate: e.target.value })} className={inputCls} />
                                <p className={helpCls}>Required for Active and On hold. A paused plan needs a review before resuming.</p>
                            </div>
                        </div>

                        <div>
                            <label className={labelCls} htmlFor="cp-summary">Summary / reason for status change <Req /></label>
                            <textarea id="cp-summary" rows={2} value={d.summary} onChange={(e) => update({ summary: e.target.value })}
                                placeholder="What changed and why? Added to the review history." className={`${inputCls} resize-y`} />
                        </div>

                        <label className="flex items-start gap-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#1b1f26] px-4 py-3 cursor-pointer">
                            <input type="checkbox" checked={d.authorised} onChange={(e) => replaceCurrent({ authorised: e.target.checked })}
                                className="mt-0.5 w-4 h-4 accent-blue-600" />
                            <span className="text-[13px] text-gray-700 dark:text-gray-300">I have reviewed the category, content and missing details, and authorise this care plan.</span>
                        </label>
                    </fieldset>
                </form>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-gray-100 dark:border-gray-800 px-6 py-3.5 bg-white dark:bg-[#262b33] rounded-b-xl">
                {feedback && (
                    <p role="status" className={`mb-2.5 text-[12.5px] ${feedback.type === 'error' ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{feedback.text}</p>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={requestClose}>{working.every((x) => x.approved) ? 'Close' : 'Cancel'}</Button>
                        {d.saved && d.planId && !d.approved && (
                            <Button variant="ghost" size="sm" onClick={discard} loading={saving === 'discard'} className="!text-rose-600">Discard draft</Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={saveDraft} disabled={locked || !!saving} loading={saving === 'draft'}>
                            {d.saved ? 'Update draft' : 'Save draft'}
                        </Button>
                        <Button type="submit" form="care-plan-form" variant="primary" disabled={locked || !!saving} loading={saving === 'approve'}>
                            Approve &amp; save
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default CarePlanEditor;
